/**
 * Componente de mensagem para o chat de teste de fluxo
 * Renderiza diferentes tipos de mensagens (bot, usuário, sistema, ações)
 */

import { TestMessage } from '@/hooks/useFlowTestEngine';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bot, User, Info, Zap, Clock, Image as ImageIcon, Video, Music, FileText, Play, ImageOff, Pause } from 'lucide-react';
import { getStorageUrl } from '@/lib/storage';
import { useState, useRef, useEffect } from 'react';
import { normalizeEmbedUrl } from '@/lib/embed-utils';

interface FlowTestMessageProps {
  message: TestMessage;
  onButtonClick?: (value: string) => void;
}

/**
 * Extrai thumbnail do YouTube
 */
const getYouTubeThumbnail = (url: string): string | null => {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
};

/**
 * Extrai extensão do arquivo da URL
 */
const getFileExtension = (url: string): string => {
  const fileName = url.split('/').pop() || '';
  const ext = fileName.split('.').pop()?.toUpperCase() || 'FILE';
  return ext;
};

/**
 * Preview de imagem
 */
function ImagePreview({ url }: { url: string }) {
  const [error, setError] = useState(false);
  const imageUrl = getStorageUrl(url) || url;

  if (error) {
    return (
      <div className="flex items-center justify-center bg-muted rounded-md p-4 h-[120px]">
        <ImageOff className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img 
      src={imageUrl}
      alt="Preview"
      className="max-h-[120px] rounded-md object-cover"
      onError={() => setError(true)}
    />
  );
}

/**
 * Preview de vídeo
 */
function VideoPreview({ url }: { url: string }) {
  const [error, setError] = useState(false);
  const videoUrl = getStorageUrl(url) || url;
  const ytThumbnail = getYouTubeThumbnail(url);

  if (error) {
    return (
      <div className="relative flex items-center justify-center bg-muted rounded-md h-[120px]">
        <Video className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  if (ytThumbnail) {
    return (
      <div className="relative rounded-md overflow-hidden">
        <img 
          src={ytThumbnail}
          alt="Video thumbnail"
          className="max-h-[120px] w-full object-cover"
          onError={() => setError(true)}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="bg-red-600 rounded-full p-3">
            <Play className="h-5 w-5 text-white fill-white" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-md overflow-hidden">
      <video 
        src={videoUrl}
        className="max-h-[120px] w-full object-cover"
        onError={() => setError(true)}
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
        <div className="bg-red-600 rounded-full p-3">
          <Play className="h-5 w-5 text-white fill-white" />
        </div>
      </div>
    </div>
  );
}

/**
 * Preview de áudio com player interativo
 */
function AudioPreview({ url }: { url: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(false);

  const audioUrl = getStorageUrl(url) || url;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handleError = () => setError(true);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="flex items-center gap-2 bg-white rounded-md p-3 opacity-50 shadow-sm">
        <Music className="h-5 w-5 text-muted-foreground" />
        <div className="text-xs text-muted-foreground">Erro ao carregar áudio</div>
      </div>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="bg-white text-gray-900 rounded-md p-3 min-w-[280px] shadow-sm">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      <div className="flex items-center gap-3">
        {/* Botão Play/Pause */}
        <button
          onClick={togglePlay}
          className="shrink-0 bg-primary text-primary-foreground rounded-full p-2 hover:opacity-90 transition-opacity"
        >
          {isPlaying ? (
            <Pause className="h-4 w-4 fill-current" />
          ) : (
            <Play className="h-4 w-4 fill-current" />
          )}
        </button>

        {/* Waveform e progresso */}
        <div className="flex-1 space-y-1.5">
          {/* Waveform visual */}
          <div className="flex gap-0.5 items-center h-4">
            {[...Array(25)].map((_, i) => {
              const barProgress = (i / 25) * 100;
              const isPassed = barProgress <= progress;
              return (
                <div 
                  key={i}
                  className={`w-0.5 rounded-full transition-colors ${
                    isPassed ? 'bg-primary' : 'bg-primary/30'
                  }`}
                  style={{ height: `${Math.random() * 12 + 4}px` }}
                />
              );
            })}
          </div>

          {/* Barra de progresso clicável */}
          <div 
            className="relative h-1 bg-muted-foreground/20 rounded-full cursor-pointer group"
            onClick={handleProgressClick}
          >
            <div 
              className="absolute h-full bg-primary rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
            {/* Thumb que aparece no hover */}
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ left: `calc(${progress}% - 6px)` }}
            />
          </div>

          {/* Tempo */}
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Preview de documento
 */
function DocumentPreview({ url }: { url: string }) {
  const extension = getFileExtension(url);
  const fileName = url.split('/').pop()?.split('?')[0] || 'documento';

  return (
    <div className="flex items-center gap-3 bg-white text-gray-900 rounded-md p-3 shadow-sm">
      <FileText className="h-8 w-8 text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{fileName}</div>
        <Badge variant="secondary" className="mt-1 text-[10px] px-1.5 py-0">
          {extension}
        </Badge>
      </div>
    </div>
  );
}

/**
 * Renderiza uma mensagem do chat de teste
 */
export function FlowTestMessage({ message, onButtonClick }: FlowTestMessageProps) {
  // Mensagem do bot
  if (message.type === 'bot') {
    const mediaType = message.metadata?.mediaType;
    const mediaUrl = message.metadata?.mediaUrl;

    return (
      <div className="flex items-start gap-2 mb-3">
        <div className="bg-white text-primary p-1.5 rounded-full shrink-0 shadow-sm border border-gray-200">
          <Bot className="h-4 w-4" />
        </div>
        <div className="flex flex-col gap-2 max-w-[80%]">
          {/* Preview de conteúdo incorporado (iframe) */}
          {message.metadata?.embedUrl && (
            <div className="mb-1 rounded-md overflow-hidden border border-border/30 shadow-sm">
              <iframe
                src={normalizeEmbedUrl(message.metadata.embedUrl as string)}
                width="100%"
                height={message.metadata.embedHeight || 400}
                style={{ border: 'none', display: 'block' }}
                sandbox="allow-scripts allow-same-origin allow-popups"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Conteúdo incorporado"
              />
            </div>
          )}

          {/* Preview de mídia */}
          {mediaType && mediaUrl && (
            <div className="mb-1">
              {mediaType === 'image' && <ImagePreview url={mediaUrl} />}
              {mediaType === 'video' && <VideoPreview url={mediaUrl} />}
              {mediaType === 'audio' && <AudioPreview url={mediaUrl} />}
              {mediaType === 'document' && <DocumentPreview url={mediaUrl} />}
            </div>
          )}

          {/* Texto da mensagem (legenda ou texto normal) */}
          {/* Texto da mensagem (legenda ou texto normal) */}
          {/* Mensagem com whitespace-pre-wrap para respeitar quebras de linha */}
          {message.content && (
            <div className="bg-white text-gray-900 rounded-lg p-3 text-sm shadow-sm whitespace-pre-wrap">
              {message.content}
            </div>
          )}
          
          {/* Botões de resposta */}
          {message.buttons && message.buttons.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {message.buttons.map(btn => (
                <Button
                  key={btn.id}
                  variant="outline"
                  size="sm"
                  className="justify-start text-left h-auto py-2.5 px-3 flex flex-col items-start gap-0.5 bg-white hover:bg-gray-50 shadow-sm border-gray-200"
                  onClick={() => onButtonClick?.(btn.text)}
                >
                  <span className="font-medium">{btn.text}</span>
                  {btn.title && (
                    <span className="text-xs text-muted-foreground font-normal">{btn.title}</span>
                  )}
                  {btn.description && (
                    <span className="text-xs text-muted-foreground font-normal">{btn.description}</span>
                  )}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Mensagem do usuário
  if (message.type === 'user') {
    return (
      <div className="flex items-start gap-2 mb-3 justify-end">
        <div className="flex flex-col gap-2 max-w-[80%]">
          {/* Balão verde estilo WhatsApp com whitespace-pre-wrap para respeitar quebras de linha */}
          <div className="bg-[#dcf8c6] text-gray-900 rounded-lg p-3 text-sm shadow-sm whitespace-pre-wrap">
            {message.content}
          </div>
        </div>
        <div className="bg-white text-primary p-1.5 rounded-full shrink-0 shadow-sm border border-gray-200">
          <User className="h-4 w-4" />
        </div>
      </div>
    );
  }

  // Mensagem de sistema
  if (message.type === 'system') {
    return (
      <div className="flex items-center justify-center mb-3">
        <Badge variant="secondary" className="gap-1.5">
          <Info className="h-3 w-3" />
          {message.content}
        </Badge>
      </div>
    );
  }

  // Mensagem de ação
  if (message.type === 'action') {
    return (
      <div className="flex items-center justify-center mb-3">
        <Badge variant="outline" className="gap-1.5 bg-green-500/10 text-green-600 border-green-500/20">
          <Zap className="h-3 w-3" />
          {message.content}
        </Badge>
      </div>
    );
  }

  // Mensagem de delay com countdown visual
  if (message.type === 'delay') {
    const delayMs = message.metadata?.delayMs || 0;
    const originalDelayMs = message.metadata?.originalDelayMs || delayMs;
    const canSkip = message.metadata?.canSkip || false;
    
    const [countdown, setCountdown] = useState(Math.ceil(delayMs / 1000));
    const [progress, setProgress] = useState(0);
    
    useEffect(() => {
      const startTime = Date.now();
      
      // Atualiza countdown e progresso
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, delayMs - elapsed);
        const newCountdown = Math.ceil(remaining / 1000);
        const newProgress = (elapsed / delayMs) * 100;
        
        setCountdown(newCountdown);
        setProgress(Math.min(100, newProgress));
        
        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 100);
      
      return () => clearInterval(interval);
    }, [delayMs]);
    
    return (
      <div className="flex flex-col items-center justify-center mb-3 gap-2 w-full max-w-xs mx-auto">
        <Badge variant="outline" className="gap-1.5 bg-amber-500/10 text-amber-600 border-amber-500/20">
          <Clock className="h-3 w-3" />
          {message.content}
        </Badge>
        
        {/* Barra de progresso */}
        <div className="w-full space-y-1.5">
          <div className="relative h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="absolute h-full bg-amber-500 rounded-full transition-all duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Contador regressivo */}
          <div className="text-center">
            <span className="text-2xl font-bold text-amber-600 tabular-nums">
              {countdown}s
            </span>
          </div>
        </div>
        
        {/* Botão de pular (se delay original > 30s) */}
        {canSkip && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              // Força conclusão do delay (a mensagem já está no histórico)
              setCountdown(0);
              setProgress(100);
            }}
          >
            Pular delay ({Math.ceil(originalDelayMs / 1000)}s original)
          </Button>
        )}
      </div>
    );
  }

  return null;
}
